import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';

import { crmApi, errCode, errMsg } from '@crm/api';
import { hasCrmPermission } from '@crm/permissions';
import { TrialDateModal } from '@crm/components/TrialDateModal';
import { TRIAL_DATE_ERROR_LABELS } from '@crm/utils/trialDate';
import { showNotificationPush } from '@shared/slices/notificationPushSlice';
import { ClientsAttendanceSection } from '@crm/pages/ClientsAttendanceSection';
import { ClientsMassStatusModal } from '@crm/pages/ClientsMassStatusModal';
import { ClientsNewClientModal } from '@crm/pages/ClientsNewClientModal';
import { ClientsQuickEditPanel } from '@crm/pages/ClientsQuickEditPanel';
import { ClientsQuickPayCell } from '@crm/pages/ClientsQuickPayCell';
import { ClientStatusSelect } from '@crm/components/ClientStatusSelect';
import { UiAutosuggest, UiButton, UiCheckbox, UiInput, UiNativeSelect } from '@shared/common/ui';

function formatDate(v) {
  if (!v) return '—';
  return v.slice(0, 10);
}

function formatRoles(roles) {
  if (!roles?.length) return '—';
  return roles
    .map((r) => (r === 'parent' ? 'Родитель' : r === 'student' ? 'Ученик' : r))
    .join(', ');
}

function formatPhoneRu(phone) {
  if (!phone) return '—';
  const d = String(phone).replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('7')) {
    return `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
  }
  return phone;
}

export function ClientsPage() {
  const { ownerId, branchId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { profile, branches } = useSelector((s) => s.crmAuth);
  const canCreateClient = hasCrmPermission(profile, branches, branchId, 'clients.create');
  const canViewClientDetail = hasCrmPermission(profile, branches, branchId, 'client.detail.view');
  const canUpdateStatus = hasCrmPermission(profile, branches, branchId, 'clients.status.update');
  const canManageDirections = hasCrmPermission(profile, branches, branchId, 'client.directions.manage');
  const canHideClient = hasCrmPermission(profile, branches, branchId, 'clients.hide');
  const canViewAttendance = hasCrmPermission(profile, branches, branchId, 'clients.attendance.view');
  const canMarkAttendance = hasCrmPermission(profile, branches, branchId, 'clients.attendance.mark');
  const canQuickPay = hasCrmPermission(profile, branches, branchId, 'clients.quick_pay.create');
  const canQuickEdit =
    canViewClientDetail && (canUpdateStatus || canManageDirections);

  const initialFilters = useMemo(
    () => ({
      fio: searchParams.get('fio') ?? '',
      phone: searchParams.get('phone') ?? '',
      status: searchParams.get('status') ?? '',
      role: searchParams.get('role') ?? '',
      directionId: searchParams.get('directionId') ?? '',
      directionLabel: searchParams.get('directionLabel') ?? '',
      relativesOnly: searchParams.get('relativesOnly') === '1',
      discountOnly: searchParams.get('discountOnly') === '1',
      showPresence: searchParams.get('showPresence') === '1',
      page: Math.max(0, Number.parseInt(searchParams.get('page') ?? '0', 10) || 0),
      sortFio: ['asc', 'desc'].includes(searchParams.get('sortFio')) ? searchParams.get('sortFio') : null,
    }),
    [], // читаем URL только при открытии страницы; дальше состояние страницы управляет URL.
  );

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [visitsMarkedToday, setVisitsMarkedToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);

  const [page, setPage] = useState(initialFilters.page);
  const limit = 10;

  const [draftFio, setDraftFio] = useState(initialFilters.fio);
  const [draftPhone, setDraftPhone] = useState(initialFilters.phone);
  const [draftStatus, setDraftStatus] = useState(initialFilters.status);
  const [draftRole, setDraftRole] = useState(initialFilters.role);
  const [draftDirectionId, setDraftDirectionId] = useState(initialFilters.directionId);
  const [draftDirectionLabel, setDraftDirectionLabel] = useState(initialFilters.directionLabel);
  const [draftRelativesOnly, setDraftRelativesOnly] = useState(initialFilters.relativesOnly);
  const [draftDiscountOnly, setDraftDiscountOnly] = useState(initialFilters.discountOnly);
  const [draftShowPresence, setDraftShowPresence] = useState(initialFilters.showPresence);

  const [applied, setApplied] = useState({
    fio: initialFilters.fio,
    phone: initialFilters.phone,
    status: initialFilters.status,
    role: initialFilters.role,
    directionId: initialFilters.directionId,
    relativesOnly: initialFilters.relativesOnly,
    discountOnly: initialFilters.discountOnly,
    showPresence: initialFilters.showPresence,
  });

  const [sortFio, setSortFio] = useState(initialFilters.sortFio);

  const [dirQuery, setDirQuery] = useState('');
  const [dirSuggestions, setDirSuggestions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [massStatusOpen, setMassStatusOpen] = useState(false);
  const [quickEditClient, setQuickEditClient] = useState(null);
  // Смена статуса, ожидающая ввода даты пробного: { row, status, mode }.
  const [trialRequest, setTrialRequest] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialError, setTrialError] = useState(null);
  const [directionDetail, setDirectionDetail] = useState(null);
  const [tariffNameById, setTariffNameById] = useState({});

  const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const currentListSearch = location.search;

  const syncSearch = useCallback(
    ({ nextPage = page, nextApplied = applied, nextSortFio = sortFio, nextDirectionLabel = draftDirectionLabel } = {}) => {
      const params = new URLSearchParams();
      if (nextPage > 0) params.set('page', String(nextPage));
      if (nextApplied.fio.trim()) params.set('fio', nextApplied.fio.trim());
      if (nextApplied.phone.trim()) params.set('phone', nextApplied.phone.trim());
      if (nextApplied.status) params.set('status', nextApplied.status);
      if (nextApplied.role) params.set('role', nextApplied.role);
      if (nextApplied.directionId) {
        params.set('directionId', nextApplied.directionId);
        if (nextDirectionLabel) params.set('directionLabel', nextDirectionLabel);
      }
      if (nextApplied.relativesOnly) params.set('relativesOnly', '1');
      if (nextApplied.discountOnly) params.set('discountOnly', '1');
      if (nextApplied.showPresence) params.set('showPresence', '1');
      if (nextSortFio) params.set('sortFio', nextSortFio);
      setSearchParams(params, { replace: true });
    },
    [applied, draftDirectionLabel, page, setSearchParams, sortFio],
  );

  useEffect(() => {
    if (!branchId) return;
    let alive = true;
    crmApi(branchId)
      .get('/settings/client_statuses')
      .then(({ data }) => {
        if (!alive) return;
        const raw = data.items ?? [];
        setStatuses(
          raw.map((it) => ({
            key: it.key,
            name: (it.value && it.value.name) || it.key,
            color:
              it.value && it.value.color && String(it.value.color).trim()
                ? String(it.value.color).trim()
                : '#dfe1e6',
            // Нужен, чтобы спросить дату пробного до смены статуса (ТЗ §4–§5).
            trialDateMode: (it.value && it.value.trial_date_mode) || 'none',
          })),
        );
      })
      .catch(() => alive && setStatuses([]));
    return () => {
      alive = false;
    };
  }, [branchId]);

  useEffect(() => {
    if (!branchId || !applied.directionId || viewMode !== 'list') {
      setDirectionDetail(null);
      return;
    }
    let alive = true;
    Promise.all([
      crmApi(branchId).get(`/directions/${applied.directionId}`),
      crmApi(branchId).get('/settings/tariffs'),
    ])
      .then(([dirRes, tarRes]) => {
        if (!alive) return;
        setDirectionDetail(dirRes.data.direction);
        const items = tarRes.data.items ?? [];
        const map = {};
        for (const it of items) {
          map[it.id] = (it.value && it.value.name) || it.key;
        }
        setTariffNameById(map);
      })
      .catch(() => alive && setDirectionDetail(null));
    return () => {
      alive = false;
    };
  }, [branchId, applied.directionId, viewMode]);

  useEffect(() => {
    if (!dirQuery.trim() || !branchId) {
      setDirSuggestions([]);
      return;
    }
    const t = setTimeout(() => {
      crmApi(branchId)
        .get('/directions', { params: { q: dirQuery.trim(), limit: 30 } })
        .then(({ data }) => setDirSuggestions(data.items ?? []))
        .catch(() => setDirSuggestions([]));
    }, 250);
    return () => clearTimeout(t);
  }, [dirQuery, branchId]);

  const fetchList = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setListError(null);
    const params = {
      limit,
      offset: page * limit,
    };
    if (applied.fio.trim()) params.fio = applied.fio.trim();
    if (applied.phone.trim()) params.phone = applied.phone.trim();
    if (applied.status) params.status = applied.status;
    if (applied.role) params.role = applied.role;
    if (applied.directionId) params.directionId = applied.directionId;
    if (applied.directionId && applied.showPresence) params.visitDay = today;
    if (applied.relativesOnly) params.relativesOnly = '1';
    if (applied.discountOnly) params.discountOnly = '1';
    if (sortFio === 'asc') params.sort = 'fio';
    if (sortFio === 'desc') params.sort = '-fio';

    try {
      const { data } = await crmApi(branchId).get('/clients', { params });
      setItems(data.items ?? []);
      setTotal(Number(data.total ?? 0));
      setVisitsMarkedToday(
        data.visitsMarkedToday !== undefined && data.visitsMarkedToday !== null
          ? Number(data.visitsMarkedToday)
          : null,
      );
    } catch (e) {
      setListError(errMsg(e, 'Не удалось загрузить клиентов'));
      setItems([]);
      setTotal(0);
      setVisitsMarkedToday(null);
    } finally {
      setLoading(false);
    }
  }, [branchId, page, applied, sortFio, today]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onApply = () => {
    const nextApplied = {
      fio: draftFio,
      phone: draftPhone,
      status: draftStatus,
      role: draftRole,
      directionId: draftDirectionId,
      relativesOnly: draftRelativesOnly,
      discountOnly: draftDiscountOnly,
      showPresence: draftShowPresence,
    };
    setPage(0);
    setApplied(nextApplied);
    syncSearch({ nextPage: 0, nextApplied, nextDirectionLabel: draftDirectionLabel });
  };

  const onClear = () => {
    setDraftFio('');
    setDraftPhone('');
    setDraftStatus('');
    setDraftRole('');
    setDraftDirectionId('');
    setDraftDirectionLabel('');
    setDraftRelativesOnly(false);
    setDraftDiscountOnly(false);
    setDraftShowPresence(false);
    setDirQuery('');
    setPage(0);
    const nextApplied = {
      fio: '',
      phone: '',
      status: '',
      role: '',
      directionId: '',
      relativesOnly: false,
      discountOnly: false,
      showPresence: false,
    };
    setApplied(nextApplied);
    syncSearch({ nextPage: 0, nextApplied, nextSortFio: sortFio, nextDirectionLabel: '' });
  };

  const selectDirection = (d) => {
    setDraftDirectionId(d.id);
    setDraftDirectionLabel(d.name);
    setDirQuery('');
  };

  const clearDirection = () => {
    setDraftDirectionId('');
    setDraftDirectionLabel('');
    setDraftShowPresence(false);
  };

  const resetDirectionFilter = () => {
    setDraftDirectionId('');
    setDraftDirectionLabel('');
    setDirQuery('');
    setDirSuggestions([]);
    setDraftShowPresence(false);
  };

  const toggleSortFio = () => {
    const nextSort = sortFio === null ? 'asc' : sortFio === 'asc' ? 'desc' : null;
    setSortFio(nextSort);
    setPage(0);
    syncSearch({ nextPage: 0, nextSortFio: nextSort });
  };

  const pageCount = Math.max(1, Math.ceil(total / limit));

  const goToPage = (nextPage) => {
    const safePage = Math.max(0, nextPage);
    setPage(safePage);
    syncSearch({ nextPage: safePage });
  };

  const toggleVisit = async (row) => {
    if (!applied.directionId) return;
    try {
      if (row.visitId) {
        await crmApi(branchId).delete(`/calendar/visitings/${row.visitId}`);
      } else {
        await crmApi(branchId).post('/calendar/visitings', {
          day: today,
          clientId: row.id,
          directionId: applied.directionId,
        });
      }
      await fetchList();
    } catch (e) {
      window.alert(errMsg(e, 'Не удалось изменить посещение'));
    }
  };

  const onStatusChange = async (row, nextStatus) => {
    // Inline только при фильтре по одному направлению (контекст однозначный).
    if (!applied.directionId || !row.clientDirectionId) {
      window.alert('Смена статуса доступна в карточке клиента или при фильтре по одному направлению');
      return;
    }

    // Статусу нужна дата пробного — спрашиваем её в модалке, а не отказом (ТЗ §4–§5).
    const mode = statuses.find((s) => s.key === nextStatus)?.trialDateMode ?? 'none';
    if (mode !== 'none') {
      setTrialRequest({ row, status: nextStatus, mode });
      return;
    }

    try {
      await crmApi(branchId).patch(
        `/clients/${row.id}/directions/${row.clientDirectionId}/status`,
        { status: nextStatus },
      );
      await fetchList();
    } catch (e) {
      window.alert(errMsg(e, 'Не удалось сохранить статус'));
    }
  };

  const confirmTrialDate = async (date) => {
    if (!trialRequest) return;
    setTrialBusy(true);
    setTrialError(null);
    try {
      await crmApi(branchId).patch(
        `/clients/${trialRequest.row.id}/directions/${trialRequest.row.clientDirectionId}/status`,
        { status: trialRequest.status, trialLessonDate: date },
      );
      setTrialRequest(null);
      await fetchList();
    } catch (e) {
      setTrialError(TRIAL_DATE_ERROR_LABELS[errCode(e)] ?? errMsg(e, 'Не удалось сохранить статус'));
    } finally {
      setTrialBusy(false);
    }
  };

  const hideClient = async (row) => {
    const name = row.fio?.trim() || 'Клиент';
    if (
      !window.confirm(
        `Скрыть «${name}» из списка? Запись останется в базе, клиент не будет отображаться (show = false).`,
      )
    ) {
      return;
    }
    try {
      await crmApi(branchId).patch(`/clients/${row.id}`, { show: false });
      await fetchList();
    } catch (e) {
      window.alert(errMsg(e, 'Не удалось скрыть клиента'));
    }
  };

  const filterCount = useMemo(() => {
    let n = 0;
    if (applied.fio.trim()) n += 1;
    if (applied.phone.trim()) n += 1;
    if (applied.status) n += 1;
    if (applied.role) n += 1;
    if (applied.directionId) n += 1;
    if (applied.relativesOnly) n += 1;
    if (applied.discountOnly) n += 1;
    if (applied.showPresence) n += 1;
    return n;
  }, [applied]);

  const showQuickPay =
    viewMode === 'list' &&
    canQuickPay &&
    Boolean(applied.directionId) &&
    Boolean(directionDetail?.tariffs?.length);

  useEffect(() => {
    if (viewMode === 'attendance' && !canViewAttendance) {
      setViewMode('list');
    }
  }, [canViewAttendance, viewMode]);

  return (
    <section className="clients-page">
      <nav className="clients-page__tabs" aria-label="Режим клиентов">
        <button
          type="button"
          className={viewMode === 'list' ? 'is-active' : ''}
          onClick={() => setViewMode('list')}
        >
          Список клиентов
        </button>
        {canViewAttendance && (
          <button
            type="button"
            className={viewMode === 'attendance' ? 'is-active' : ''}
            onClick={() => setViewMode('attendance')}
          >
            Таблица посещений
          </button>
        )}
      </nav>

      {viewMode === 'attendance' && <ClientsAttendanceSection branchId={branchId} />}

      {viewMode === 'list' && (
        <>
      <header className="page-header page-header--spread clients-page__header">
        <h1>
          Клиенты{' '}
          {total > 0 ? <span style={{ color: 'var(--color-muted)' }}>({total})</span> : null}
        </h1>
        <div className="clients-page__header-actions">
          {canUpdateStatus ? (
            <UiButton type="button" variant="ghost" size="sm" onClick={() => setMassStatusOpen(true)}>
              Массовая смена статуса
            </UiButton>
          ) : null}
          <div className="clients-page__header-pager">
            <button
              type="button"
              className="clients-page__header-nav clients-page__header-nav--prev"
              disabled={page <= 0}
              onClick={() => goToPage(page - 1)}
              aria-label="Предыдущая страница"
              title="Предыдущая страница"
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.68734 7.27317C9.43735 7.02006 9.43733 6.60968 9.68729 6.35655C9.93726 6.10342 10.3425 6.1034 10.5925 6.3565L15.4725 11.2973C15.8572 11.6868 15.8572 12.3132 15.4725 12.7027L10.5925 17.6435C10.3425 17.8966 9.93726 17.8966 9.68729 17.6435C9.43733 17.3904 9.43735 16.98 9.68734 16.7269L14.3559 12L9.68734 7.27317Z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <span className="clients-page__selected-hint">
              {page + 1} / {pageCount}
            </span>
            <button
              type="button"
              className="clients-page__header-nav clients-page__header-nav--next"
              disabled={page + 1 >= pageCount}
              onClick={() => goToPage(page + 1)}
              aria-label="Следующая страница"
              title="Следующая страница"
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M9.68734 7.27317C9.43735 7.02006 9.43733 6.60968 9.68729 6.35655C9.93726 6.10342 10.3425 6.1034 10.5925 6.3565L15.4725 11.2973C15.8572 11.6868 15.8572 12.3132 15.4725 12.7027L10.5925 17.6435C10.3425 17.8966 9.93726 17.8966 9.68729 17.6435C9.43733 17.3904 9.43735 16.98 9.68734 16.7269L14.3559 12L9.68734 7.27317Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
          {canCreateClient && (
            <UiButton
              type="button"
              className="clients-page__add-btn"
              onClick={() => setNewClientOpen(true)}
              aria-label="Новый клиент"
              title="Новый клиент"
            >
              <svg width="16" height="18" viewBox="0 0 14 14" fill="none" aria-hidden>
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7 0C6.58579 0 6.25 0.335786 6.25 0.75V6.25H0.75C0.335786 6.25 0 6.58579 0 7C0 7.41421 0.335786 7.75 0.75 7.75H6.25V13.25C6.25 13.6642 6.58579 14 7 14C7.41421 14 7.75 13.6642 7.75 13.25V7.75H13.25C13.6642 7.75 14 7.41421 14 7C14 6.58579 13.6642 6.25 13.25 6.25H7.75V0.75C7.75 0.335786 7.41421 0 7 0Z"
                  fill="currentColor"
                />
              </svg>
            </UiButton>
          )}
        </div>
      </header>

      <div className="clients-page__filter">
        <div className="clients-page__filter-title">
          <span>Фильтр</span>
          {filterCount > 0 ? (
            <span className="clients-page__filter-badge">{filterCount}</span>
          ) : null}
        </div>
        <div className="clients-page__filter-grid">
          <UiInput label="Ф.И.О." value={draftFio} onChange={(e) => setDraftFio(e.target.value)} />
          <UiInput
            label="Телефон"
            type="number"
            value={draftPhone}
            onChange={(e) => setDraftPhone(e.target.value)}
            placeholder="Введите часть номера"
          />
          <UiAutosuggest
            className="field clients-page__autosuggest"
            label="Направление"
            value={draftDirectionId ? draftDirectionLabel : dirQuery}
            onChange={(value) => {
              if (draftDirectionId) clearDirection();
              setDirQuery(value);
            }}
            suggestions={dirSuggestions}
            onSelect={selectDirection}
            placeholder="Начните вводить название…"
            onClear={resetDirectionFilter}
            emptyText=""
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => item.name}
          />
          <div className="field">
            <label>Статус в направлении</label>
            <ClientStatusSelect
              className="clients-page__filter-status"
              value={draftStatus}
              onChange={setDraftStatus}
              statuses={statuses}
              emptyLabel="Выберите из списка"
            />
          </div>
          <UiNativeSelect
            label="Роль"
            value={draftRole}
            onChange={(e) => setDraftRole(e.target.value)}
          >
            <option value="">Все</option>
            <option value="student">Ученик</option>
            <option value="parent">Родитель</option>
            <option value="both">Ученик + Родитель</option>
            <option value="none">Без роли</option>
          </UiNativeSelect>
        </div>
        <div className="clients-page__filter-row">
          <UiButton type="button"  onClick={onApply}>
            Применить
          </UiButton>
          <UiButton variant="ghost" type="button"  onClick={onClear}>
            Очистить
          </UiButton>
          <UiCheckbox
            className="clients-page__check"
            label="Подгрузить родственников"
            title="Показать всех клиентов филиала, включая родителей без направлений"
            checked={draftRelativesOnly}
            onChange={(e) => setDraftRelativesOnly(e.target.checked)}
          />
          <UiCheckbox
            className="clients-page__check"
            label="Есть постоянная скидка"
            checked={draftDiscountOnly}
            onChange={(e) => setDraftDiscountOnly(e.target.checked)}
          />
          <UiCheckbox
            className="clients-page__check"
            label="Показать присутствие"
            checked={draftShowPresence}
            onChange={(e) => setDraftShowPresence(e.target.checked)}
            disabled={!draftDirectionId || !canViewAttendance}
          />
          {applied.directionId && visitsMarkedToday !== null ? (
            <span className="clients-page__visit-hint">
              Отмечено сегодня: {visitsMarkedToday}
            </span>
          ) : null}
        </div>
      </div>

      {listError && <div className="form-error clients-page__list-error">{listError}</div>}

      <div className="clients-page__table-wrap">
        <table className="data-table clients-page__table">
          <thead>
            <tr>
              {canQuickEdit ? (
                <th className="clients-page__col-quick-edit" aria-label="Быстрое редактирование" />
              ) : null}
              <th className="clients-page__col-name">
                <button type="button" className="clients-page__sort" onClick={toggleSortFio}>
                  Имя
                  {sortFio === 'asc' ? ' ↑' : null}
                  {sortFio === 'desc' ? ' ↓' : null}
                </button>
              </th>
              <th>Email</th>
              <th>Телефон</th>
              <th>Роли</th>
              <th>Направления</th>
              <th>Общий статус</th>
              <th>Дата рождения</th>
              <th className="clients-page__col-num">Оплаченных занятий</th>
              {applied.directionId && applied.showPresence ? <th>Посещение</th> : null}
              {showQuickPay ? <th className="clients-page__col-quick">Оплата</th> : null}
              <th className="clients-page__col-actions" />
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                {canQuickEdit ? (
                  <td className="clients-page__col-quick-edit">
                    <UiButton
                      type="button"
                      className="clients-page__icon-btn"
                      title="Быстрое редактирование направлений"
                      aria-label="Быстрое редактирование направлений"
                      onClick={() => setQuickEditClient({ id: c.id, fio: c.fio })}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <rect x="3" y="3" width="7" height="7" rx="1" />
                        <rect x="14" y="3" width="7" height="7" rx="1" />
                        <rect x="3" y="14" width="7" height="7" rx="1" />
                        <path d="M14 17h7" />
                        <path d="M17.5 14v7" />
                      </svg>
                    </UiButton>
                  </td>
                ) : null}
                <td>
                  {canViewClientDetail ? (
                    <Link to={`/${ownerId}/crm/${branchId}/clients/${c.id}${currentListSearch}`} className="clients-page__name-link">
                      {c.fio ?? '(без имени)'}
                    </Link>
                  ) : (
                    c.fio ?? '(без имени)'
                  )}
                </td>
                <td className="clients-page__td-nowrap">
                  {(() => {
                    const list =
                      c.emails && c.emails.length > 0 ? c.emails : c.login ? [c.login] : [];
                    if (!list.length) return '—';
                    return list.map((em, idx) => (
                      <div key={`${c.id}-email-${idx}`} className="clients-page__email-row">
                        <span className="clients-page__email-text" title={em}>
                          {em}
                        </span>
                        <CopyToClipboard
                          text={em}
                          onCopy={() =>
                            dispatch(showNotificationPush({ content: 'Email скопирован!' }))
                          }
                        >
                          <button
                            type="button"
                            className="clients-page__copy-email"
                            title="Скопировать в буфер"
                            aria-label={`Скопировать ${em}`}
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden
                            >
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                          </button>
                        </CopyToClipboard>
                      </div>
                    ));
                  })()}
                </td>
                <td className="clients-page__td-nowrap">{formatPhoneRu(c.phone)}</td>
                <td>{formatRoles(c.roles)}</td>
                <td className="clients-page__dirs-td" title={c.directionsLabel ?? ''}>
                  {(() => {
                    const items = Array.isArray(c.directionsItems) ? c.directionsItems : [];
                    if (!items.length) return '—';
                    const visible = items.slice(0, 3);
                    const extra = items.length - visible.length;
                    return (
                      <>
                        {visible.map((d, idx) => {
                          const meta = statuses.find((s) => String(s.key) === String(d.status));
                          const statusText = d.statusName || meta?.name || d.status || '';
                          const statusColor = meta?.color?.trim() || '';
                          return (
                            <div key={`${c.id}-dir-${idx}`} className="clients-page__dir-line">
                              <span className="clients-page__dir-name">{d.name}</span>
                              {statusText ? (
                                <span className="clients-page__dir-status">
                                  {statusColor ? (
                                    <span
                                      className="clients-page__dir-status-dot"
                                      style={{ backgroundColor: statusColor }}
                                      aria-hidden
                                    />
                                  ) : null}
                                  {statusText}
                                </span>
                              ) : null}
                            </div>
                          );
                        })}
                        {extra > 0 ? (
                          <div className="clients-page__dir-more">ещё {extra}</div>
                        ) : null}
                      </>
                    );
                  })()}
                </td>
                <td className="clients-page__status-td">
                  {(() => {
                    const canEditStatus =
                      Boolean(applied.directionId) &&
                      canUpdateStatus &&
                      Boolean(c.clientDirectionId);
                    const roles = Array.isArray(c.roles) ? c.roles : [];
                    const isPureParent =
                      roles.includes('parent') && !roles.includes('student');
                    // Пустой статус не подписываем ролью — роль уже в колонке «Роли».
                    const emptyLabel = isPureParent ? '—' : 'Без направления';
                    return (
                      <ClientStatusSelect
                        className="clients-page__table-status"
                        value={c.status ?? ''}
                        onChange={(key) => onStatusChange(c, key)}
                        statuses={statuses}
                        allowEmpty={!c.status}
                        emptyLabel={emptyLabel}
                        fallbackName={c.statusName}
                        readOnly={!canEditStatus}
                      />
                    );
                  })()}
                </td>
                <td>{formatDate(c.birthday)}</td>
                <td className="clients-page__col-num">{c.passes ?? 0}</td>
                {applied.directionId && applied.showPresence ? (
                  <td className="clients-page__col-visit">
                    <button
                      type="button"
                      className={`btn btn-sm clients-page__visit-btn ${c.visitId ? 'is-on' : ''}`}
                      title={c.visitId ? 'Снять отметку' : 'Отметить присутствие'}
                      disabled={!canMarkAttendance}
                      onClick={() => toggleVisit(c)}
                    >
                      ✓
                    </button>
                  </td>
                ) : null}
                {showQuickPay ? (
                  <td className="clients-page__col-quick">
                    <ClientsQuickPayCell
                      branchId={branchId}
                      client={c}
                      directionId={applied.directionId}
                      directionName={directionDetail?.name ?? ''}
                      tariffs={directionDetail.tariffs}
                      tariffNameById={tariffNameById}
                      onPaid={fetchList}
                    />
                  </td>
                ) : null}
                <td className="clients-page__col-actions">
                  <div className="clients-page__row-actions">
                    {canViewClientDetail && (
                      <Link
                        to={`/${ownerId}/crm/${branchId}/clients/${c.id}${currentListSearch}`}
                        className="clients-page__icon-btn"
                        title="Открыть карточку"
                        aria-label="Открыть карточку"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                        </svg>
                      </Link>
                    )}
                    {canHideClient && (
                      <UiButton
                        type="button"
                        className="clients-page__icon-btn clients-page__icon-btn--danger"
                        title="Скрыть клиента из списка"
                        aria-label="Скрыть клиента из списка"
                        onClick={() => hideClient(c)}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          <line x1="10" y1="11" x2="10" y2="17" />
                          <line x1="14" y1="11" x2="14" y2="17" />
                        </svg>
                      </UiButton>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!items.length && !loading && (
              <tr>
                <td
                  colSpan={
                    9 +
                    (canQuickEdit ? 1 : 0) +
                    (applied.directionId && applied.showPresence ? 1 : 0) +
                    (showQuickPay ? 1 : 0)
                  }
                  style={{ textAlign: 'center', color: 'var(--color-muted)' }}
                >
                  {filterCount ? 'Клиенты по данному фильтру не найдены' : 'Клиентов пока нет'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

        </>
      )}

      <ClientsNewClientModal open={newClientOpen} onClose={() => setNewClientOpen(false)} />
      <ClientsMassStatusModal
        open={massStatusOpen}
        onClose={() => setMassStatusOpen(false)}
        statuses={statuses}
      />
      <ClientsQuickEditPanel
        open={Boolean(quickEditClient)}
        clientId={quickEditClient?.id}
        clientFio={quickEditClient?.fio}
        statuses={statuses}
        canManageDirections={canManageDirections}
        canUpdateStatus={canUpdateStatus}
        listSearch={currentListSearch}
        onClose={() => setQuickEditClient(null)}
        onChanged={fetchList}
      />
      {trialRequest && (
        <TrialDateModal
          title="Для этого статуса нужно указать дату пробного занятия"
          subtitle={`${trialRequest.row.fio ?? 'Клиент'} → ${
            statuses.find((s) => s.key === trialRequest.status)?.name ?? trialRequest.status
          }`}
          mode={trialRequest.mode}
          currentDate={trialRequest.row.trialLessonDate}
          today={today}
          busy={trialBusy}
          error={trialError}
          submitLabel="Сохранить"
          onSubmit={confirmTrialDate}
          onClose={() => {
            setTrialRequest(null);
            setTrialError(null);
          }}
        />
      )}
    </section>
  );
}
